// Freigabe-Workflow (Vier-Augen-Prinzip): Typen und Abfragen (Spiegel von apps/core/src/contracts/approvals.ts).
import { useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useTenantQuery } from './queries';

export type ApprovalTargetType = 'playlist' | 'layout' | 'schedule';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export interface ApprovalState {
  enabled: boolean;
  canApprove: boolean;
  canSelfApprove: boolean;
  /** Offene Anfragen, über die der Benutzer entscheiden darf (Zähler im Menü). */
  toDecide: number;
  /** Eigene offene Anfragen. */
  mine: number;
}

export interface ApprovalSettings { enabled: boolean; allowSelfApproval: boolean; notifyEmail: boolean; adminCount: number; rowVersion: number }

export interface ApprovalRequest {
  id: string; targetType: ApprovalTargetType; targetId: string; targetName: string; targetExists: boolean; status: ApprovalStatus; note: string | null;
  submittedBy: { id: string | null; label: string }; submittedAt: string;
  decidedBy: { id: string | null; label: string } | null; decidedAt: string | null; decisionComment: string | null;
  baseRevisionNo: number | null; resultRevisionNo: number | null; canDecide: boolean; canWithdraw: boolean;
}

export type EntryValues = Record<string, string | number | boolean | null>;
export interface ComparisonEntry {
  change: 'added' | 'removed' | 'changed' | 'unchanged'; kind: 'media' | 'layout' | 'rule'; label: string; changedKeys: string[];
  before: EntryValues | null; after: EntryValues | null;
}
export interface ApprovalComparison {
  publishedRevisionNo: number | null;
  fields: { key: string; before: string | null; after: string | null; changed: boolean }[];
  entries: ComparisonEntry[];
  layout: { orientation: 'landscape' | 'portrait'; submitted: unknown; published: unknown } | null;
}
export interface ApprovalDetail extends ApprovalRequest { comparison: ApprovalComparison }

export const approvalKeys = {
  all: ['approvals'] as const,
  status: ['approvals', 'status'] as const,
  settings: ['approvals', 'settings'] as const,
  list: (status: string) => ['approvals', 'list', status] as const,
  one: (id: string) => ['approvals', 'one', id] as const,
};

/** Zustand für Menü-Zähler und „Einreichen statt Veröffentlichen“; alle 30 s aktualisiert. */
export const useApprovalState = () => useTenantQuery(approvalKeys.status, () => api.get<ApprovalState>('/approvals/status'), { refetchInterval: 30_000 });

export const useApprovalSettings = (enabled = true) =>
  useTenantQuery(approvalKeys.settings, () => api.get<ApprovalSettings>('/approvals/settings'), { enabled });

export const useApprovals = (status: 'pending' | 'done') =>
  useTenantQuery(approvalKeys.list(status), async () => (await api.get<{ items: ApprovalRequest[] }>(`/approvals?status=${status}`)).items);

export const useApproval = (id: string) => useTenantQuery(approvalKeys.one(id), () => api.get<ApprovalDetail>(`/approvals/${id}`));

/** Offene Anfrage zu einem Objekt (Status „Wartet auf Freigabe“), oder undefined. */
export function usePendingFor(type: ApprovalTargetType, id: string | undefined): ApprovalRequest | undefined {
  const { data: state } = useApprovalState();
  const { data } = useTenantQuery(approvalKeys.list('pending'), async () => (await api.get<{ items: ApprovalRequest[] }>('/approvals?status=pending')).items,
    { enabled: !!state && (state.enabled || state.mine > 0 || state.toDecide > 0) });
  return id ? data?.find((r) => r.targetType === type && r.targetId === id) : undefined;
}

/** Offene Anfragen als Zuordnung Objekt-ID → Anfrage (für Listen). */
export function usePendingMap(): Map<string, ApprovalRequest> {
  const { data: state } = useApprovalState();
  const { data } = useTenantQuery(approvalKeys.list('pending'), async () => (await api.get<{ items: ApprovalRequest[] }>('/approvals?status=pending')).items,
    { enabled: !!state && (state.enabled || state.mine > 0 || state.toDecide > 0) });
  return new Map((data ?? []).map((r) => [r.targetId, r]));
}

/** Nach Einreichen/Entscheiden alle Freigabe-Daten neu laden. */
export function useRefreshApprovals() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: approvalKeys.all });
}

export const submitPath = (type: ApprovalTargetType, id: string) => `/${({ playlist: 'playlists', layout: 'layouts', schedule: 'schedules' } as const)[type]}/${id}/approval-requests`;

/** Pfad zum Objekt im Manager. */
export const targetPath = (type: ApprovalTargetType, id: string) => ({ playlist: `/playlists/${id}`, layout: `/studio/${id}`, schedule: `/schedules/${id}` }[type]);
